import React, { useState, useEffect } from 'react';
import { dashboardConfigAPI } from '../../services/api';
import { 
  LayoutDashboard, Eye, EyeOff, GripVertical, Settings, Save, 
  RefreshCw, ChevronUp, ChevronDown, Plus, Trash2, Check
} from 'lucide-react';

const WidgetDashboardConfig = () => {
  const [widgets, setWidgets] = useState([]);
  const [referentiels, setReferentiels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [showConfig, setShowConfig] = useState(null);
  const [villesMeteo, setVillesMeteo] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [widgetsRes, refRes] = await Promise.all([
        dashboardConfigAPI.getWidgets(),
        dashboardConfigAPI.getReferentiels()
      ]);
      setWidgets(widgetsRes.data);
      setReferentiels(refRes.data);
      setVillesMeteo(refRes.data.villes_meteo || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleWidget = async (code) => {
    try {
      const res = await dashboardConfigAPI.toggleWidget(code);
      setWidgets(widgets.map(w => 
        w.code === code ? { ...w, visible: res.data.visible } : w
      ));
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const moveWidget = async (index, direction) => {
    const newWidgets = [...widgets];
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= widgets.length) return;
    
    [newWidgets[index], newWidgets[newIndex]] = [newWidgets[newIndex], newWidgets[index]];
    
    // Mettre à jour les ordres
    const orders = newWidgets.map((w, i) => ({ code: w.code, ordre: i }));
    
    setWidgets(newWidgets);
    
    try {
      await dashboardConfigAPI.reorderWidgets(orders);
    } catch (error) {
      console.error('Erreur:', error);
      loadData();
    }
  };

  const saveWidgetConfig = async (code, config) => {
    setSaving(true);
    try {
      await dashboardConfigAPI.updateWidget(code, { config });
      await loadData();
      setShowConfig(null);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const resetWidgets = async () => {
    if (!window.confirm('Réinitialiser tous les widgets à leurs valeurs par défaut ?')) return;
    try {
      await dashboardConfigAPI.resetWidgets();
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <LayoutDashboard className="w-6 h-6 text-green-600" />
            Configuration du Dashboard
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Activez, désactivez et réorganisez les widgets de votre tableau de bord
          </p>
        </div>
        <button
          onClick={resetWidgets}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <RefreshCw className="w-4 h-4" />
          Réinitialiser
        </button>
      </div>

      {/* Liste des widgets */}
      <div className="space-y-3">
        {widgets.map((widget, index) => (
          <div
            key={widget.code}
            className={`bg-white rounded-lg border-2 transition ${
              widget.visible 
                ? 'border-green-200 shadow-sm' 
                : 'border-gray-200 opacity-60'
            }`}
          >
            <div className="p-4">
              <div className="flex items-center gap-4">
                {/* Drag handle / Ordre */}
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={() => moveWidget(index, -1)}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <GripVertical className="w-5 h-5 text-gray-300" />
                  <button
                    onClick={() => moveWidget(index, 1)}
                    disabled={index === widgets.length - 1}
                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                {/* Icône */}
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: widget.couleur + '20' }}
                >
                  <LayoutDashboard className="w-6 h-6" style={{ color: widget.couleur }} />
                </div>

                {/* Infos */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{widget.label}</h3>
                    {widget.systeme && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                        Système
                      </span>
                    )}
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                      {widget.largeur === 'full' ? 'Pleine largeur' : 'Demi-largeur'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{widget.description}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowConfig(showConfig === widget.code ? null : widget.code)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                    title="Configurer"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => toggleWidget(widget.code)}
                    className={`p-2 rounded-lg transition ${
                      widget.visible 
                        ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                    title={widget.visible ? 'Masquer' : 'Afficher'}
                  >
                    {widget.visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Configuration expandée */}
              {showConfig === widget.code && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <WidgetConfigPanel 
                    widget={widget}
                    referentiels={referentiels}
                    villesMeteo={villesMeteo}
                    onSave={(config) => saveWidgetConfig(widget.code, config)}
                    saving={saving}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <div className="flex items-center gap-4">
          <span><strong>{widgets.filter(w => w.visible).length}</strong> widgets actifs</span>
          <span>•</span>
          <span><strong>{widgets.filter(w => !w.visible).length}</strong> widgets masqués</span>
        </div>
      </div>
    </div>
  );
};

// Composant de configuration par widget
const WidgetConfigPanel = ({ widget, referentiels, villesMeteo, onSave, saving }) => {
  const [config, setConfig] = useState(widget.config || {});

  const handleSave = () => {
    onSave(config);
  };

  // Rendu spécifique selon le type de widget
  const renderConfigFields = () => {
    switch (widget.code) {
      case 'header_bienvenue':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Texte motivant</label>
              <input
                type="text"
                value={config.texte_motivant || 'Prêt pour une nouvelle journée productive ?'}
                onChange={(e) => setConfig({ ...config, texte_motivant: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Ex: Prêt pour une nouvelle journée productive ?"
              />
              <p className="text-xs text-gray-500 mt-1">Ce texte s'affiche sous le message de bienvenue</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ville pour la météo</label>
              <select
                value={config.ville_meteo || 'paris'}
                onChange={(e) => setConfig({ ...config, ville_meteo: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                {villesMeteo.map(ville => (
                  <option key={ville.code} value={ville.code}>{ville.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">La météo en temps réel de cette ville sera affichée</p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.afficher_date !== false}
                onChange={(e) => setConfig({ ...config, afficher_date: e.target.checked })}
                className="w-4 h-4 text-green-600 rounded"
              />
              <span>Afficher la date du jour</span>
            </label>
          </div>
        );

      case 'stats_globales':
        const statsDisponibles = referentiels.stats_disponibles || [];
        const statsVisibles = config.stats_visibles || ['total', 'new', 'inprog', 'done'];
        return (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">Statistiques à afficher</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {statsDisponibles.map(stat => (
                <label key={stat.code} className="flex items-center gap-2 cursor-pointer p-2 bg-white rounded border hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={statsVisibles.includes(stat.code)}
                    onChange={(e) => {
                      const newStats = e.target.checked
                        ? [...statsVisibles, stat.code]
                        : statsVisibles.filter(s => s !== stat.code);
                      setConfig({ ...config, stats_visibles: newStats });
                    }}
                    className="w-4 h-4 text-green-600 rounded"
                  />
                  <span className="text-sm">{stat.label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'taches_prioritaires':
      case 'charge_equipe':
      case 'activite_recente':
      case 'alertes_documents':
      case 'rappels_taches':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre d'éléments</label>
              <input
                type="number"
                min="1"
                max="20"
                value={config.limite || 5}
                onChange={(e) => setConfig({ ...config, limite: parseInt(e.target.value) })}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={config.afficher_vide !== false}
                onChange={(e) => setConfig({ ...config, afficher_vide: e.target.checked })}
                className="w-4 h-4 text-green-600 rounded"
              />
              <span>Afficher même si vide</span>
            </label>
          </div>
        );

      case 'dossiers_recents':
        const colonnesDisponibles = referentiels.colonnes_dossiers || [];
        const colonnesVisibles = config.colonnes || ['reference', 'client', 'caisse', 'statut', 'priorite', 'gestionnaire'];
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de dossiers</label>
              <input
                type="number"
                min="1"
                max="20"
                value={config.limite || 5}
                onChange={(e) => setConfig({ ...config, limite: parseInt(e.target.value) })}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Colonnes à afficher</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {colonnesDisponibles.map(col => (
                  <label key={col.code} className="flex items-center gap-2 cursor-pointer p-2 bg-white rounded border hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={colonnesVisibles.includes(col.code)}
                      onChange={(e) => {
                        const newCols = e.target.checked
                          ? [...colonnesVisibles, col.code]
                          : colonnesVisibles.filter(c => c !== col.code);
                        setConfig({ ...config, colonnes: newCols });
                      }}
                      className="w-4 h-4 text-green-600 rounded"
                    />
                    <span className="text-sm">{col.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <p className="text-gray-500 text-sm">Pas de configuration disponible pour ce widget</p>
        );
    }
  };

  return (
    <div className="space-y-4">
      {renderConfigFields()}
      
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
};

export default WidgetDashboardConfig;
